import React from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Checkbox,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import TemplateSelector from "./TemplateSelector";
import OverlayOptions from "./OverlayOptions";
import ClippingVideoPreview from "./ClippingVideoPreview";
import { parseYoutubeUrl } from "../videoSourceUtils";

const PREFERRED_LENGTHS = [
  { id: "under30sec", label: "Under 30s" },
  { id: "under60sec", label: "Under 60s" },
  { id: "under90sec", label: "Under 90s" },
  { id: "under3min", label: "Under 3 min" },
  { id: "under5min", label: "Under 5 min" },
  { id: "under10min", label: "Under 10 min" },
];

const LANGUAGES = [
  "en", "es", "fr", "de", "it", "pt", "ru", "ja", "ko", "zh", "ar", "hi",
  "tr", "pl", "nl", "sv", "no", "da", "fi", "cs", "vi", "id", "fa", "uk",
];

const LAYOUTS = ["auto", "fill", "fit", "square"];

export function defaultClippingForm(assets) {
  return {
    sourceType: "youtube",
    url: "",
    fileUrl: "",
    start: 0,
    end: 120,
    preferredLength: "under60sec",
    language: "en",
    captionLanguage: "en",
    templateId: assets?.defaultTemplateId || "",
    layout: "auto",
    noClipping: false,
    hookTitle: false,
    music: false,
    musicName: "",
    musicVolume: 50,
    gameVideo: false,
    gameVideoName: "",
    memeHook: false,
    memeHookName: "",
    ctaEnabled: false,
    ctaText: "",
  };
}

function validateForm(form) {
  const hasUrl = form.sourceType === "youtube" && String(form.url || "").trim();
  const hasFile = form.sourceType === "file" && String(form.fileUrl || "").trim();
  if (!hasUrl && !hasFile) return "Provide a YouTube URL or a hosted file URL.";
  if (hasUrl && String(form.url).includes("/shorts/")) {
    return "YouTube Shorts URLs are not supported.";
  }
  if (hasFile && !/^https:\/\//i.test(String(form.fileUrl).trim())) {
    return "File URL must start with https://";
  }
  const start = Number(form.start);
  const end = Number(form.end);
  if (!Number.isFinite(start) || start < 0) return "Start time must be 0 or greater.";
  if (!Number.isFinite(end) || end <= start) return "End time must be greater than start.";
  const window = end - start;
  if (window < 60 || window > 1200) {
    return "Clip window must be 60–1,200 seconds (1–20 minutes).";
  }
  if (form.ctaEnabled && String(form.ctaText || "").length > 200) {
    return "CTA text must be 200 characters or fewer.";
  }
  return null;
}

function buildPayload(form) {
  const payload = {
    start: Number(form.start),
    end: Number(form.end),
    preferredLength: form.preferredLength,
    language: form.language,
    captionLanguage: form.captionLanguage,
    layout: form.layout,
    noClipping: Boolean(form.noClipping),
    hookTitle: Boolean(form.hookTitle),
  };

  if (form.sourceType === "youtube") {
    payload.url = String(form.url).trim();
  } else {
    payload.fileUrl = String(form.fileUrl).trim();
  }

  if (form.templateId) payload.templateId = form.templateId;

  if (form.music) {
    payload.music = true;
    if (form.musicName) payload.musicName = form.musicName;
    payload.musicVolume = Number(form.musicVolume) || 50;
  }
  if (form.gameVideo) {
    payload.gameVideo = true;
    if (form.gameVideoName) payload.gameVideoName = form.gameVideoName;
  }
  if (form.memeHook) {
    payload.memeHook = true;
    if (form.memeHookName) payload.memeHookName = form.memeHookName;
  }
  if (form.ctaEnabled) {
    payload.ctaEnabled = true;
    if (form.ctaText) payload.ctaText = form.ctaText.trim();
  }

  return payload;
}

export default function ClippingForm({
  assets,
  initialForm,
  disabled = false,
  onSubmit,
  lang,
}) {
  const [form, setForm] = React.useState(() => initialForm || defaultClippingForm(assets));
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (assets?.defaultTemplateId && !form.templateId) {
      setForm((f) => ({ ...f, templateId: assets.defaultTemplateId }));
    }
  }, [assets?.defaultTemplateId, form.templateId]);

  React.useEffect(() => {
    if (initialForm) setForm(initialForm);
  }, [initialForm]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleYoutubeUrlChange = (value) => {
    const parsed = parseYoutubeUrl(value);
    const patch = { url: value };
    if (parsed && !parsed.error && parsed.startSec > 0) {
      patch.start = parsed.startSec;
      const currentEnd = Number(form.end);
      if (!Number.isFinite(currentEnd) || currentEnd <= parsed.startSec) {
        patch.end = parsed.startSec + 120;
      }
    }
    set(patch);
  };

  const previewUrl = form.sourceType === "youtube" ? form.url : form.fileUrl;

  const handleSubmit = () => {
    const msg = validateForm(form);
    if (msg) {
      setError(msg);
      return;
    }
    setError("");
    onSubmit?.(buildPayload(form));
  };

  const windowSec = Math.max(0, Number(form.end) - Number(form.start));

  return (
    <Stack spacing={2.5} sx={{ opacity: disabled ? 0.65 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {lang?.clippingVideoSource || "Video source"}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={form.sourceType}
          onChange={(_, v) => v && set({ sourceType: v })}
          sx={{ mb: 1.5 }}
        >
          <ToggleButton value="youtube">{lang?.clippingYoutube || "YouTube URL"}</ToggleButton>
          <ToggleButton value="file">{lang?.clippingFileUrl || "Hosted file URL"}</ToggleButton>
        </ToggleButtonGroup>
        {form.sourceType === "youtube" ? (
          <TextField
            fullWidth
            size="small"
            placeholder="https://www.youtube.com/watch?v=…"
            value={form.url}
            onChange={(e) => handleYoutubeUrlChange(e.target.value)}
          />
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="https://cdn.example.com/video.mp4"
              value={form.fileUrl}
              onChange={(e) => set({ fileUrl: e.target.value })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
              {lang?.clippingFileUrlHint ||
                "URL must stay accessible for 1–2 hours during processing."}
            </Typography>
          </>
        )}
        <ClippingVideoPreview
          sourceType={form.sourceType}
          url={previewUrl}
          startSec={Number(form.start) || 0}
          lang={lang}
        />
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label={lang?.clippingStart || "Start (seconds)"}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 0 }}
          value={form.start}
          onChange={(e) => set({ start: e.target.value })}
        />
        <TextField
          label={lang?.clippingEnd || "End (seconds)"}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 1 }}
          value={form.end}
          onChange={(e) => set({ end: e.target.value })}
        />
      </Stack>
      <Typography variant="caption" color={windowSec >= 60 && windowSec <= 1200 ? "text.secondary" : "error.main"}>
        {lang?.clippingWindow || "Window"}: {windowSec}s ({lang?.clippingWindowRange || "60–1,200s required"})
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
            {lang?.clippingPreferredLength || "Preferred clip length"}
          </Typography>
          <Select
            fullWidth
            size="small"
            value={form.preferredLength}
            onChange={(e) => set({ preferredLength: e.target.value })}
          >
            {PREFERRED_LENGTHS.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
            {lang?.clippingLayout || "Layout"}
          </Typography>
          <Select fullWidth size="small" value={form.layout} onChange={(e) => set({ layout: e.target.value })}>
            {LAYOUTS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
            {lang?.clippingLanguage || "Spoken language"}
          </Typography>
          <Select fullWidth size="small" value={form.language} onChange={(e) => set({ language: e.target.value })}>
            {LANGUAGES.map((l) => (
              <MenuItem key={l} value={l}>
                {l.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
            {lang?.clippingCaptionLanguage || "Caption language"}
          </Typography>
          <Select
            fullWidth
            size="small"
            value={form.captionLanguage}
            onChange={(e) => set({ captionLanguage: e.target.value })}
          >
            {LANGUAGES.map((l) => (
              <MenuItem key={l} value={l}>
                {l.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Stack>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {lang?.clippingCaptionTemplate || "Caption template"}
        </Typography>
        <TemplateSelector
          templates={assets.templates}
          selectedId={form.templateId}
          defaultTemplateId={assets.defaultTemplateId}
          onSelect={(id) => set({ templateId: id })}
          lang={lang}
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox checked={Boolean(form.noClipping)} onChange={(e) => set({ noClipping: e.target.checked })} />
        }
        label={lang?.clippingNoClipping || "Process full range as one clip (skip AI highlight detection)"}
      />

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {lang?.clippingOverlays || "Overlays & music"}
        </Typography>
        <OverlayOptions form={form} onChange={setForm} assets={assets} lang={lang} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        startIcon={<AutoAwesomeOutlined />}
        onClick={handleSubmit}
        disabled={disabled}
        data-tour="ai-clipping-generate"
        sx={{ alignSelf: "flex-start", borderRadius: 2, fontWeight: 700, px: 3 }}
      >
        {lang?.clippingSubmit || "Generate clips"}
      </Button>
    </Stack>
  );
}
